import { Box, MenuItem, TextField, TextFieldProps } from '@mui/material'
import React from 'react'
import { Controller, FieldValues, Path, useFormContext, useWatch } from 'react-hook-form'

import { Option } from '@/app/component/form/types'

/**
 * A select that, when the special "other" option is chosen, shows a text input.
 * The bound form value is always a single string: either the selected option's value
 * or the typed "other" text.
 */
export type RHFSelectOtherProps<T extends FieldValues> = {
  name: Path<T>
  label?: string
  options: ReadonlyArray<Option>
  /** The option value that should trigger the text field. */
  otherValue: string
  /** Label for the text field shown when otherValue is selected. */
  otherTextLabel?: string
} & Pick<TextFieldProps, 'required' | 'disabled' | 'autoFocus'>

export function RHFSelectOther<T extends FieldValues>({
  name,
  label,
  options,
  otherValue,
  otherTextLabel = 'Please specify',
  ...props
}: RHFSelectOtherProps<T>) {
  const { control } = useFormContext<T>()
  const [isOther, setIsOther] = React.useState<boolean>(false)
  const watchedValue = useWatch({ control, name }) as string | undefined

  const valueIsKnownOption = React.useMemo(
    () => !!watchedValue && options.some((o) => o.value === watchedValue),
    [options, watchedValue],
  )
  React.useEffect(() => {
    // If the current value is not a known option, switch to "other" mode
    if (watchedValue) {
      setIsOther(!valueIsKnownOption)
    }
  }, [valueIsKnownOption, watchedValue])

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        const selectValue = isOther ? otherValue : field.value || ''

        return (
          <Box display="flex" flexDirection="column" gap={2} mb={2}>
            <TextField
              {...field}
              error={!!error}
              fullWidth
              helperText={error?.message}
              label={label}
              select
              size="small"
              slotProps={{
                inputLabel: { shrink: true },
                select: { onClose: field.onBlur },
              }}
              variant="standard"
              value={selectValue}
              onChange={(e) => {
                const selected = e.target.value
                if (selected === otherValue) {
                  // Switch to "other" mode; clear value to let user type
                  setIsOther(true)
                  if (valueIsKnownOption) {
                    field.onChange('')
                  }
                } else {
                  setIsOther(false)
                  field.onChange(selected)
                }
              }}
              {...props}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value} aria-label={option.label} disabled={option.disabled}>
                  {option.label}
                </MenuItem>
              ))}
              <MenuItem value={otherValue} aria-label={otherTextLabel}>
                {otherTextLabel}
              </MenuItem>
            </TextField>
            {isOther && (
              <TextField
                error={!!error}
                fullWidth
                helperText={error?.message}
                label={otherTextLabel}
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ mt: 1 }}
                variant="standard"
                value={field.value || ''}
                onBlur={field.onBlur}
                onChange={(e) => field.onChange(e.target.value)}
                {...props}
              />
            )}
          </Box>
        )
      }}
    />
  )
}
